import type {
  DealDrawerActivityFilter,
  DealDrawerActivityItem,
  DealDrawerActivityKind,
} from "@/types/v2/conversation"

// Single source of truth for which activity kinds belong
// in each filter bucket. The deal show page tabs and the
// activity-feed component both consume this mapping —
// keep them in lock-step or the tab counts will drift from
// the visible rows. Every non-message/note/call kind MUST
// land here: a kind in no bucket counts toward "All" but
// is unreachable from every tab (the exhaustiveness test
// in __tests__/deal-activity-filter.test.ts enforces this).
const UPDATE_KINDS = new Set<DealDrawerActivityKind>([
  "created",
  "stage",
  "value",
  "status",
  "name",
  "assignment",
  // Follow-up audit events ("Follow-up created" /
  // "Follow-up updated") from the backend ItemSerializer.
  "activity",
])

export function matchesFilter(
  item: DealDrawerActivityItem,
  filter: DealDrawerActivityFilter,
): boolean {
  switch (filter) {
    case "all":
      return true
    case "messages":
      return item.kind === "message"
    case "notes":
      return item.kind === "note"
    case "calls":
      return item.kind === "call"
    case "updates":
      return UPDATE_KINDS.has(item.kind)
  }
}

export function filterActivity(
  items: DealDrawerActivityItem[],
  filter: DealDrawerActivityFilter,
): DealDrawerActivityItem[] {
  if (filter === "all") return items
  return items.filter((item) => matchesFilter(item, filter))
}

export interface ActivityCounts {
  all: number
  messages: number
  notes: number
  calls: number
  updates: number
}

export function countActivity(
  items: DealDrawerActivityItem[],
): ActivityCounts {
  const counts: ActivityCounts = {
    all: items.length,
    messages: 0,
    notes: 0,
    calls: 0,
    updates: 0,
  }
  for (const item of items) {
    if (item.kind === "message") counts.messages += 1
    else if (item.kind === "note") counts.notes += 1
    else if (item.kind === "call") counts.calls += 1
    else if (UPDATE_KINDS.has(item.kind)) counts.updates += 1
  }
  return counts
}

export const EMPTY_STATE_COPY: Record<
  DealDrawerActivityFilter,
  string
> = {
  all: "Nothing happening yet. Activity will show up here as the deal moves.",
  messages:
    "No messages on this deal yet. WhatsApp, email, and SMS exchanges will land here.",
  notes:
    "No notes yet. Capture context, decisions, and call summaries here.",
  calls:
    "No calls yet. Voice calls and their AI summaries will land here.",
  updates:
    "No updates yet. Stage moves, value edits, and AI actions will land here.",
}
